/**
 * Service for managing franchisee compliance data
 */

import { auth } from '../firebase'; // Import auth to get the current user's token

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';

/**
 * Get compliance data for a franchisee
 * @param {string} franchiseeId - The franchisee ID
 * @returns {Promise<object>} - Compliance data
 */
export const getComplianceData = async (franchiseeId) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const response = await fetch(`${API_BASE_URL}/fse/compliance/get`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ franchiseeId }),
    });

    const result = await response.json();

    if (result.status === 'success') {
      return result.data;
    } else {
      throw new Error(result.message || 'Failed to get compliance data.');
    }
  } catch (error) {
    console.error('Error fetching compliance data:', error);
    throw error;
  }
};

/**
 * Update compliance data for a franchisee
 * @param {string} franchiseeId - The franchisee ID
 * @param {object} complianceData - The compliance data to update
 * @returns {Promise<object>} - Updated compliance data
 */
export const updateComplianceData = async (franchiseeId, complianceData) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const response = await fetch(`${API_BASE_URL}/fse/compliance/update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ franchiseeId, complianceData }),
    });

    const result = await response.json();

    if (result.status === 'success') {
      return result.data;
    } else {
      throw new Error(result.message || 'Failed to update compliance data.');
    }
  } catch (error) {
    console.error('Error updating compliance data:', error);
    throw error;
  }
};

/**
 * Upload police certificate file
 * @param {File} file - The file to upload
 * @returns {Promise<string>} - The URL of the uploaded file
 */
export const uploadPoliceCertificate = async (file) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('category', 'police-certificates');

    const response = await fetch(`${API_BASE_URL}/upload/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (result.status === 'success') {
      // Handle both response formats
      if (result.urls && result.urls.length > 0) {
        return result.urls[0];
      } else if (result.data && result.data.urls && result.data.urls.length > 0) {
        return result.data.urls[0];
      } else {
        throw new Error('No file URL returned from upload.');
      }
    } else {
      throw new Error(result.message || 'Failed to upload police certificate.');
    }
  } catch (error) {
    console.error('Error uploading police certificate:', error);
    throw error;
  }
};

/**
 * Delete a police certificate file
 * @param {string} fileUrl - The URL of the file to delete
 * @returns {Promise<void>}
 */
export const deletePoliceCertificate = async (fileUrl) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const response = await fetch(`${API_BASE_URL}/upload/delete-file`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileUrl }),
    });

    const result = await response.json();

    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to delete police certificate.');
    }
  } catch (error) {
    console.error('Error deleting police certificate:', error);
    throw error;
  }
};

/**
 * Upload certificate of currency file
 * @param {File} file - The file to upload
 * @returns {Promise<string>} - The URL of the uploaded file
 */
export const uploadCertificateOfCurrency = async (file) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('category', 'certificate-of-currency');

    const response = await fetch(`${API_BASE_URL}/upload/files`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
      },
      body: formData,
    });

    const result = await response.json();

    if (result.status === 'success') {
      // Handle both response formats
      if (result.urls && result.urls.length > 0) {
        return result.urls[0];
      } else if (result.data && result.data.urls && result.data.urls.length > 0) {
        return result.data.urls[0];
      } else {
        throw new Error('No file URL returned from upload.');
      }
    } else {
      throw new Error(result.message || 'Failed to upload certificate of currency.');
    }
  } catch (error) {
    console.error('Error uploading certificate of currency:', error);
    throw error;
  }
};

/**
 * Delete a certificate of currency file
 * @param {string} fileUrl - The URL of the file to delete
 * @returns {Promise<void>}
 */
export const deleteCertificateOfCurrency = async (fileUrl) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error("User not authenticated.");
  }

  const idToken = await user.getIdToken();

  try {
    const response = await fetch(`${API_BASE_URL}/upload/delete-file`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileUrl }),
    });

    const result = await response.json();

    if (result.status !== 'success') {
      throw new Error(result.message || 'Failed to delete certificate of currency.');
    }
  } catch (error) {
    console.error('Error deleting certificate of currency:', error);
    throw error;
  }
};

/**
 * Get a fresh signed URL for an existing document in GCS.
 * Avoids using stale stored signed URLs that may fail with SignatureDoesNotMatch.
 * @param {string} fileUrl - The stored file URL (signed URL or GCS object path)
 * @returns {Promise<string>} - A fresh signed URL valid for 60 minutes
 */
export const getDocumentSignedUrl = async (fileUrl) => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated.');
  }

  const idToken = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}/upload/signed-url`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fileUrl }),
  });

  const result = await response.json();

  if (result.status === 'success') {
    return result.data.signedUrl;
  } else {
    throw new Error(result.message || 'Failed to get document URL.');
  }
};
